import React, { useState, useRef, useEffect } from "react";
import { Lead, Presupuesto } from "@/api/entities";
import { invokeFunctions } from "@/api/integrations";
import { auth } from "@/api/firebaseClient";
import { useAuth } from "@/lib/AuthContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, ArrowRight, ArrowRightLeft, Building2, Mail, Phone, MessageSquare, Clock, FileText, CreditCard, CheckCircle, X as XIcon, ExternalLink } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ToastAction } from "@/components/ui/toast";
import RegistrarPagoDialog from "../components/leads/RegistrarPagoDialog";
import CierreLeadDialog from "../components/leads/CierreLeadDialog";
import LeadInfo from "../components/lead-detail/LeadInfo";
import SeguimientoPanel from "../components/lead-detail/SeguimientoPanel";
import FacturaDirectaPanel from "../components/lead-detail/FacturaDirectaPanel";
import PresupuestoGenerator from "../components/lead-detail/PresupuestoGenerator";
import Timeline from "../components/lead-detail/Timeline";
import PresupuestosList from "../components/lead-detail/PresupuestosList";
import Comments from "../components/lead-detail/Comments";
import EmailConversation from "../components/lead-detail/EmailConversation";
import { useToast } from "@/components/ui/use-toast";
import { ESTADOS_LEADS, normalizeEstadoLead, ESTADOS_NO_PENDIENTES, shouldShowPendienteEmail } from "@/lib/estadosLeads";

const ESTADOS_PEDIDOS = {
  nuevo_pedido: { label: "Nuevo Pedido", color: "bg-green-500" },
  preparando_archivos: { label: "Preparando Archivos", color: "bg-blue-500" },
  en_produccion: { label: "En Producción", color: "bg-purple-500" },
  finalizado: { label: "Finalizado", color: "bg-yellow-500" },
  enviado: { label: "Enviado", color: "bg-orange-500" },
  facturado: { label: "Facturado", color: "bg-gray-500" }
};

export default function LeadDetail() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const urlParams = new URLSearchParams(window.location.search);
  const leadId = urlParams.get('id');
  const tabParam = urlParams.get('tab');
  const expandEmailParam = urlParams.get('expandEmail');
  const presupuestoGeneratorRef = useRef(null);
  // 'comentarios' ya no es una pestaña — ahora se muestra en el panel lateral.
  // Si una URL vieja apuntaba a ?tab=comentarios, caemos al default 'emails'.
  const VALID_TABS = ["emails", "presupuesto", "historial", "timeline"];
  const [activeTab, setActiveTab] = useState(
    tabParam && VALID_TABS.includes(tabParam) ? tabParam : "emails"
  );
  const [showPagoDialog, setShowPagoDialog] = useState(false);
  const [registrandoPago, setRegistrandoPago] = useState(false);
  const [showCierreDialog, setShowCierreDialog] = useState(false);
  const [cerrandoLead, setCerrandoLead] = useState(false);
  const [showEmbudoDialog, setShowEmbudoDialog] = useState(false);
  const [showFacturadoDialog, setShowFacturadoDialog] = useState(false);

  // #5/#13 Modo foco: navegación secuencial dentro de la columna del Kanban
  const [focus, setFocus] = useState(() => {
    try {
      const raw = sessionStorage.getItem('kanban_focus');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // expira tras 6h por seguridad
      if (!parsed || !Array.isArray(parsed.columnLeadIds) || (Date.now() - (parsed.ts || 0) > 6 * 3600 * 1000)) return null;
      return parsed;
    } catch (e) { return null; }
  });
  const focusIndex = focus ? focus.columnLeadIds.indexOf(leadId) : -1;
  // Mapa rápido id→lead leído del cache de React Query (poblado por Dashboard).
  // Permite filtrar los siguientes IDs por estado actual, en lugar de fiarnos
  // ciegamente del snapshot que se guardó en sessionStorage al entrar al foco.
  const leadsCache = queryClient.getQueryData(['leads']) || [];
  const leadsById = React.useMemo(() => {
    const m = new Map();
    for (const l of leadsCache) m.set(l.id, l);
    return m;
  }, [leadsCache]);
  // Un ID candidato sigue en la columna del foco si en cache su embudo es
  // 'recibidos' y su estado normalizado coincide con focus.columnEstado.
  // Si el lead ya no está en cache, se asume pendiente (conservador: no lo
  // saltamos para no perder leads).
  const stillInColumn = (id) => {
    if (!focus) return false;
    if (id === leadId) return false;
    const cand = leadsById.get(id);
    if (!cand) return true;
    const inRecibidos = (cand.embudo || 'recibidos') === 'recibidos';
    if (!inRecibidos) return false;
    return normalizeEstadoLead(cand.estado) === focus.columnEstado;
  };
  const nextLeadId = focus && focusIndex >= 0
    ? (focus.columnLeadIds.slice(focusIndex + 1).find(stillInColumn) || null)
    : null;
  const prevLeadId = focus && focusIndex > 0
    ? ([...focus.columnLeadIds.slice(0, focusIndex)].reverse().find(stillInColumn) || null)
    : null;

  const goToLead = (targetId) => {
    if (!targetId) return;
    navigate(createPageUrl(`Lead?id=${targetId}`));
  };
  const exitFocus = () => {
    try { sessionStorage.removeItem('kanban_focus'); } catch {}
    setFocus(null);
  };

  const { data: lead, isLoading, refetch: refetchLead } = useQuery({
    queryKey: ['lead', leadId],
    queryFn: async () => {
      if (!leadId) return null;
      return Lead.get(leadId);
    },
    enabled: !!leadId,
  });

  const { data: presupuestos = [] } = useQuery({
    queryKey: ['presupuestos', leadId],
    queryFn: () => Presupuesto.list({ lead_id: leadId }, { orderBy: 'created_date', orderDirection: 'desc' }),
    enabled: !!leadId,
  });

  const updateLeadMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      // User is handled inside Lead.update or use auth.currentUser
      return Lead.update(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });

  const deleteLeadMutation = useMutation({
    mutationFn: async (id) => {
      // Eliminar todos los presupuestos del lead
      const presupuestosLead = await Presupuesto.list({ lead_id: id });
      for (const p of presupuestosLead) {
        await Presupuesto.delete(p.id);
      }
      // Eliminar el lead
      return Lead.delete(id);
    },
    onSuccess: () => {
      toast({
        title: "Lead eliminado",
        description: "El lead ha sido eliminado correctamente",
      });
      // #5/#13 Si estamos en modo foco, saltar al siguiente lead de la columna
      if (focus && nextLeadId) {
        goToLead(nextLeadId);
      } else {
        navigate(createPageUrl("Dashboard"));
      }
    },
  });

  // Snapshot de los campos que las acciones de estado/embudo pueden tocar.
  // Lo usamos para el botón "Deshacer" en los toasts: restaura el lead al
  // estado previo a la acción y añade una entrada de auditoría al historial.
  const captureLeadSnapshot = (l) => ({
    estado: l.estado,
    embudo: l.embudo || 'recibidos',
    pendiente_email: l.pendiente_email ?? false,
    cerrado_motivo: l.cerrado_motivo ?? '',
    cerrado_fecha: l.cerrado_fecha ?? '',
    cerrado_auto: l.cerrado_auto ?? false,
  });

  const undoLeadChange = async (snapshot) => {
    if (!leadId) return;
    // Releer el lead fresco del cache para no perder cambios paralelos al
    // historial; sobre eso añadimos la entrada de "acción deshecha".
    const currentLead = queryClient.getQueryData(['lead', leadId]) || lead;
    const historialEstados = [...((currentLead && currentLead.historial_estados) || [])];
    historialEstados.push({
      estado: snapshot.estado,
      fecha: new Date().toISOString(),
      usuario: 'Usuario',
      motivo: 'acción deshecha por usuario',
    });
    await updateLeadMutation.mutateAsync({
      id: leadId,
      data: {
        estado: snapshot.estado,
        embudo: snapshot.embudo,
        pendiente_email: snapshot.pendiente_email,
        cerrado_motivo: snapshot.cerrado_motivo,
        cerrado_fecha: snapshot.cerrado_fecha,
        cerrado_auto: snapshot.cerrado_auto,
        historial_estados: historialEstados,
      },
    });
    toast({
      title: 'Acción deshecha',
      description: 'Se restauró el estado anterior del lead.',
      duration: 3000,
    });
  };

  const showUndoToast = ({ title, description, snapshot }) => {
    toast({
      title,
      description,
      duration: 6000,
      action: (
        <ToastAction altText="Deshacer" onClick={() => undoLeadChange(snapshot)}>
          Deshacer
        </ToastAction>
      ),
    });
  };

  const handleEstadoChange = async (newEstado) => {
    if (!lead) return;

    // Cierre manual: pedir motivo en dialog antes de aplicar.
    if (newEstado === 'cerrado') {
      setShowCierreDialog(true);
      return;
    }

    const snapshot = captureLeadSnapshot(lead);
    const historialEstados = lead.historial_estados || [];
    const estadoAnterior = lead.estado;

    historialEstados.push({
      estado: newEstado,
      fecha: new Date().toISOString(),
      usuario: "Usuario"
    });

    const updateData = {
      estado: newEstado,
      historial_estados: historialEstados
    };

    // Si pasa a nuevo_pedido, cambiar embudo a pedidos
    if (newEstado === 'nuevo_pedido') {
      updateData.embudo = 'pedidos';
    }

    // Al cerrar (o salir del embudo recibidos), limpiar el flag de pendiente
    // de respuesta — la conversación ya no está bajo seguimiento activo.
    if (ESTADOS_NO_PENDIENTES.includes(newEstado) || newEstado === 'nuevo_pedido') {
      updateData.pendiente_email = false;
    }

    await updateLeadMutation.mutateAsync({
      id: leadId,
      data: updateData
    });

    const tabla = (newEstado === 'nuevo_pedido' || lead.embudo === 'pedidos') ? ESTADOS_PEDIDOS : ESTADOS_LEADS;
    const newLabel = tabla[newEstado]?.label || newEstado;
    showUndoToast({
      title: 'Estado actualizado',
      description: `Lead movido a "${newLabel}".`,
      snapshot,
    });

    // Enviar alerta a Discord
    try {
      await invokeFunctions('sendDiscordAlert', {
        evento: 'cambio_estado',
        lead_id: leadId,
        datos_extra: {
          estado_anterior: estadoAnterior,
          estado_nuevo: newEstado
        }
      });
    } catch (error) {
      console.error('Error enviando alerta Discord:', error);
    }
  };

  const handleConfirmarCierre = async (motivo) => {
    if (!lead) return;
    setCerrandoLead(true);
    try {
      const snapshot = captureLeadSnapshot(lead);
      const historialEstados = lead.historial_estados || [];
      const estadoAnterior = lead.estado;
      historialEstados.push({
        estado: 'cerrado',
        fecha: new Date().toISOString(),
        usuario: "Usuario",
        motivo,
      });
      await updateLeadMutation.mutateAsync({
        id: leadId,
        data: {
          estado: 'cerrado',
          cerrado_motivo: motivo,
          cerrado_auto: false,
          cerrado_fecha: new Date().toISOString(),
          pendiente_email: false,
          historial_estados: historialEstados,
        }
      });
      setShowCierreDialog(false);
      showUndoToast({
        title: 'Lead cerrado',
        description: `Motivo: ${motivo}.`,
        snapshot,
      });
      try {
        await invokeFunctions('sendDiscordAlert', {
          evento: 'cambio_estado',
          lead_id: leadId,
          datos_extra: { estado_anterior: estadoAnterior, estado_nuevo: 'cerrado', motivo },
        });
      } catch (error) {
        console.error('Error enviando alerta Discord:', error);
      }
    } finally {
      setCerrandoLead(false);
    }
  };

  const handleMarcarGanado = async () => {
    if (!lead) return;

    const snapshot = captureLeadSnapshot(lead);
    const historialEstados = lead.historial_estados || [];
    historialEstados.push({
      estado: 'nuevo_pedido',
      fecha: new Date().toISOString(),
      usuario: "Usuario"
    });

    await updateLeadMutation.mutateAsync({
      id: leadId,
      data: {
        estado: 'nuevo_pedido',
        embudo: 'pedidos',
        historial_estados: historialEstados,
        pendiente_email: false,
      }
    });

    showUndoToast({
      title: '¡Oportunidad ganada!',
      description: 'El lead ha pasado al embudo de pedidos.',
      snapshot,
    });
  };

  const handleRegistrarPago = async (metodoPago) => {
    if (!lead) return;
    setRegistrandoPago(true);

    try {
      await updateLeadMutation.mutateAsync({
        id: leadId,
        data: { 
          pagado: true,
          metodo_pago: metodoPago,
          fecha_pago: new Date().toISOString()
        }
      });

      toast({
        title: "Pago registrado",
        description: `Pago por ${metodoPago === 'transferencia' ? 'transferencia bancaria' : metodoPago === 'tarjeta' ? 'tarjeta de crédito' : 'efectivo'} registrado correctamente`,
      });
      setShowPagoDialog(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo registrar el pago",
        variant: "destructive"
      });
    }
    setRegistrandoPago(false);
  };

  const handleAddComment = async (texto) => {
    if (!lead) return;

    // const user = auth.currentUser; // Emulate with useAuth user
    // Ya tenemos 'user' del contexto
    const comentarios = lead.comentarios || [];
    comentarios.push({
      texto,
      fecha: new Date().toISOString(),
      usuario: user?.email || "Usuario"
    });

    await updateLeadMutation.mutateAsync({
      id: leadId,
      data: { comentarios }
    });
  };

  const handleArchiveComment = async (comentario) => {
    if (!lead) return;
    
    const comentariosActualizados = (lead.comentarios || []).map(c => {
      if (c.fecha === comentario.fecha && c.texto === comentario.texto) {
        return { ...c, archivado: true };
      }
      return c;
    });

    await updateLeadMutation.mutateAsync({
      id: leadId,
      data: { comentarios: comentariosActualizados }
    });
  };

  const handleDeleteLead = async () => {
    await deleteLeadMutation.mutateAsync(leadId);
  };

  const handleCambiarEmbudo = async () => {
    if (!lead) return;
    const snapshot = captureLeadSnapshot(lead);
    const actual = lead.embudo || 'recibidos';
    const destino = actual === 'prospeccion' ? 'recibidos' : 'prospeccion';
    await updateLeadMutation.mutateAsync({
      id: leadId,
      data: { embudo: destino },
    });
    setShowEmbudoDialog(false);
    showUndoToast({
      title: 'Embudo actualizado',
      description: `Lead movido a ${destino === 'prospeccion' ? 'Prospección' : 'Recibidos'}.`,
      snapshot,
    });
  };

  const [presupuestoParaCargar, setPresupuestoParaCargar] = useState(null);

  // #5/#13 Auto-avance: si el lead inicialmente coincidía con la columna del foco
  // y, tras una acción, deja de coincidir → saltar al siguiente automáticamente.
  const initialMatchedRef = useRef(null);
  useEffect(() => { initialMatchedRef.current = null; }, [leadId]);
  useEffect(() => {
    if (!lead || !focus) return;
    // El foco solo aplica al embudo "recibidos".
    const inRecibidos = (lead.embudo || 'recibidos') === 'recibidos';
    const currentNorm = inRecibidos ? normalizeEstadoLead(lead.estado) : '__OTRO__';
    const matches = currentNorm === focus.columnEstado;
    if (initialMatchedRef.current === null) {
      initialMatchedRef.current = matches;
      return;
    }
    if (initialMatchedRef.current && !matches) {
      const t = setTimeout(() => {
        if (nextLeadId) {
          toast({ title: "Siguiente lead", description: "Avanzando al siguiente automáticamente." });
          goToLead(nextLeadId);
        } else {
          // Columna vaciada — salimos del foco y volvemos al Dashboard, en vez de
          // quedarnos viendo un lead que ya no pertenece a la columna original.
          toast({ title: "Columna vacía", description: "No quedan más leads en esta columna." });
          exitFocus();
          navigate(createPageUrl("Dashboard"));
        }
      }, 700);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.estado, lead?.embudo, leadId]);

  const handleCargarPresupuesto = (presupuesto) => {
    // Forzar re-render cambiando el objeto con timestamp
    setPresupuestoParaCargar({ ...presupuesto, _timestamp: Date.now() });
    setActiveTab("presupuesto");
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 dark:bg-gray-800 rounded"></div>
        </div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[50vh] space-y-4">
        <div className="text-xl font-semibold text-gray-500">Lead no encontrado</div>
        <p className="text-gray-400">El ID del lead solicitado no existe en la base de datos.</p>
        <Button onClick={() => navigate(createPageUrl("Dashboard"))}>
          Volver al Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
          <Button
            variant="ghost"
            onClick={() => navigate(createPageUrl("Dashboard"))}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al CRM
          </Button>
          {focus && focusIndex >= 0 && (
            <div className="flex items-center gap-2 bg-indigo-50 dark:bg-indigo-950 border border-indigo-200 dark:border-indigo-800 rounded-md px-2 py-1">
              <span className="text-xs text-indigo-700 dark:text-indigo-300 font-medium px-1">
                Foco: {ESTADOS_LEADS[focus.columnEstado]?.label || focus.columnEstado}
                <span className="ml-2 text-indigo-500/70">{focusIndex + 1}/{focus.columnLeadIds.length}</span>
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={!prevLeadId}
                onClick={() => goToLead(prevLeadId)}
                className="h-7 px-2"
                title="Anterior lead de la columna"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={!nextLeadId}
                onClick={() => goToLead(nextLeadId)}
                className="h-7 px-2"
                title="Siguiente lead de la columna"
              >
                <ArrowRight className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={exitFocus}
                className="h-7 px-2 text-indigo-500/70 hover:text-red-500"
                title="Salir del modo foco"
              >
                <XIcon className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{lead.nombre}</h1>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
              {lead.empresa && (
                <p className="text-gray-500 dark:text-gray-400">{lead.empresa}</p>
              )}
              {(() => {
                // Defensa: hay leads históricos con created_date corrupto
                // (strings tipo "usuario:Usuario}" o "fecha:2026-..." por una mala
                // serialización de la migración Base44). Si new Date(...) no es
                // válida, no mostramos badge en lugar de pintar "Invalid Date".
                if (!lead.created_date) return null;
                const d = new Date(lead.created_date);
                if (isNaN(d.getTime())) return null;
                const fmt = d.toLocaleString('es-ES', {
                  day: '2-digit', month: '2-digit', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                  timeZone: 'Europe/Madrid',
                });
                return (
                  <span
                    className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400"
                    title={`Entró al CRM: ${d.toLocaleString('es-ES', { timeZone: 'Europe/Madrid' })}`}
                  >
                    <Clock className="w-3 h-3" />
                    {fmt}
                  </span>
                );
              })()}
              {lead.pagado && (
                <span className="inline-flex items-center gap-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-xs px-2 py-1 rounded-full">
                  <CheckCircle className="w-3 h-3" />
                  Pagado
                </span>
              )}
              {/* #19/4 — Indicador FD a nivel de ficha. Verde si hay contacto sincronizado;
                  gris/punteado si no. Si existe link al contacto en FD, el badge es clicable. */}
              {(() => {
                const fdId = lead.factura_directa_id || lead.factura_directa_sandbox_id;
                const fdLink = lead.factura_directa_link || lead.factura_directa_sandbox_link;
                if (fdId && fdLink) {
                  return (
                    <a
                      href={fdLink}
                      target="_blank"
                      rel="noreferrer"
                      title="Contacto sincronizado en Facturadirecta — abrir"
                      className="inline-flex items-center gap-1 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-xs px-2 py-1 rounded-full hover:bg-green-200 dark:hover:bg-green-900/60 transition-colors"
                    >
                      <FileText className="w-3 h-3" />
                      FD
                      <CheckCircle className="w-3 h-3" />
                      <ExternalLink className="w-3 h-3 opacity-60" />
                    </a>
                  );
                }
                return (
                  <span
                    title="Contacto NO sincronizado con Facturadirecta"
                    className="inline-flex items-center gap-1 border border-dashed border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 text-xs px-2 py-1 rounded-full"
                  >
                    <FileText className="w-3 h-3" />
                    FD
                  </span>
                );
              })()}
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Cambiar embudo entre Recibidos y Prospección. Sólo en embudos de leads
                (no en pedidos), con confirmación porque mueve el lead entre listas. */}
            {lead.embudo !== 'pedidos' && (() => {
              const actual = lead.embudo || 'recibidos';
              const destino = actual === 'prospeccion' ? 'recibidos' : 'prospeccion';
              const destinoLabel = destino === 'prospeccion' ? '🎯 Prospección' : '📥 Recibidos';
              return (
                <Button
                  onClick={() => setShowEmbudoDialog(true)}
                  variant="outline"
                  title={`Mover a ${destinoLabel}`}
                >
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Mover a {destinoLabel}
                </Button>
              );
            })()}

            {/* Marcar como contestado: mover manualmente a 'respondido' (columna En Curso)
                cuando Abraham ha respondido por otro canal (teléfono, WhatsApp...) sin
                enviar email desde el CRM. Solo visible si está en 'nuevo'. */}
            {lead.embudo !== 'pedidos' && normalizeEstadoLead(lead.estado) === 'nuevo' && (
              <Button
                onClick={() => handleEstadoChange('respondido')}
                variant="outline"
                className="border-purple-500 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Marcar como contestado
              </Button>
            )}

            {/* Botón Marcar como Ganado - solo en embudos de leads */}
            {lead.embudo !== 'pedidos' && ['presupuesto_enviado', 'respondido'].includes(normalizeEstadoLead(lead.estado)) && (
              <Button
                onClick={handleMarcarGanado}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Marcar Ganado
              </Button>
            )}
            
            {/* Botón Pagado - desde presupuesto_enviado en leads, siempre en pedidos */}
            {!lead.pagado && (
              (lead.embudo === 'pedidos' || ['presupuesto_enviado', 'respondido'].includes(normalizeEstadoLead(lead.estado)))
            ) && (
              <Button
                onClick={() => setShowPagoDialog(true)}
                variant="outline"
                className="border-green-500 text-green-600 hover:bg-green-50 dark:hover:bg-green-950"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Pagado
              </Button>
            )}

            {/* Marcar como Facturado: estado final del embudo pedidos.
                Pasa lead.estado a 'facturado' con confirmación; el toast con
                "Deshacer" lo provee handleEstadoChange. */}
            {lead.embudo === 'pedidos' && lead.estado !== 'facturado' && (
              <Button
                onClick={() => setShowFacturadoDialog(true)}
                variant="outline"
                className="border-blue-500 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950"
              >
                <FileText className="w-4 h-4 mr-2" />
                Marcar como Facturado
              </Button>
            )}

            {(() => {
              const tabla = lead.embudo === 'pedidos' ? ESTADOS_PEDIDOS : ESTADOS_LEADS;
              const estadoEfectivo = lead.embudo === 'pedidos' ? lead.estado : normalizeEstadoLead(lead.estado);
              const meta = tabla[estadoEfectivo];
              return (
                <span className={`${meta?.color || 'bg-gray-500'} text-white px-4 py-2 rounded-full text-sm font-medium`}>
                  {meta?.label || estadoEfectivo}
                </span>
              );
            })()}
            <select
              value={lead.embudo === 'pedidos' ? lead.estado : normalizeEstadoLead(lead.estado)}
              onChange={(e) => handleEstadoChange(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            >
              {Object.entries(lead.embudo === 'pedidos' ? ESTADOS_PEDIDOS : ESTADOS_LEADS).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
              {lead.embudo !== 'pedidos' && (
                <option value="nuevo_pedido">→ Marcar como Ganado</option>
              )}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <LeadInfo
            lead={lead}
            onUpdate={updateLeadMutation.mutateAsync}
            onDelete={handleDeleteLead}
          />
          {/* Comentarios del equipo debajo del email original del cliente.
              Orden visual: email original (read-only en LeadInfo) → comentarios → input. */}
          <Comments
            comentarios={lead.comentarios || []}
            onAddComment={handleAddComment}
            onArchiveComment={handleArchiveComment}
          />
          <SeguimientoPanel leadId={leadId} />
          <FacturaDirectaPanel lead={lead} onUpdate={updateLeadMutation.mutateAsync} />
        </div>

        <div className="lg:col-span-2">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-4 dark:bg-gray-800">
                <TabsTrigger value="emails" className="relative">
                  Emails
                  {shouldShowPendienteEmail(lead) && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="presupuesto">Presupuesto</TabsTrigger>
                <TabsTrigger value="historial">Historial</TabsTrigger>
                <TabsTrigger value="timeline">Timeline</TabsTrigger>
              </TabsList>

            <TabsContent value="emails">
                <EmailConversation lead={lead} expandEmailId={expandEmailParam} />
              </TabsContent>

              <TabsContent value="presupuesto">
                <PresupuestoGenerator 
                  ref={presupuestoGeneratorRef} 
                  leadId={leadId} 
                  lead={lead}
                  presupuestoParaCargar={presupuestoParaCargar}
                />
              </TabsContent>

              <TabsContent value="historial">
              <PresupuestosList 
                presupuestos={presupuestos} 
                leadId={leadId} 
                onCargarPresupuesto={handleCargarPresupuesto}
              />
            </TabsContent>

            <TabsContent value="timeline">
              <Timeline lead={lead} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <RegistrarPagoDialog
        open={showPagoDialog}
        onClose={() => setShowPagoDialog(false)}
        onSubmit={handleRegistrarPago}
        isLoading={registrandoPago}
      />

      <CierreLeadDialog
        open={showCierreDialog}
        onClose={() => setShowCierreDialog(false)}
        onSubmit={handleConfirmarCierre}
        isLoading={cerrandoLead}
      />

      <AlertDialog open={showFacturadoDialog} onOpenChange={setShowFacturadoDialog}>
        <AlertDialogContent className="dark:bg-gray-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-900 dark:text-white">
              ¿Marcar este pedido como facturado?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600 dark:text-gray-400">
              El pedido pasará al estado "Facturado", el último del embudo. Podrás deshacer la acción desde el toast.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                setShowFacturadoDialog(false);
                await handleEstadoChange('facturado');
              }}
            >
              Marcar como facturado
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showEmbudoDialog} onOpenChange={setShowEmbudoDialog}>
        <AlertDialogContent className="dark:bg-gray-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-900 dark:text-white">
              ¿Mover este lead a {(lead?.embudo || 'recibidos') === 'prospeccion' ? 'Recibidos' : 'Prospección'}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600 dark:text-gray-400">
              {(lead?.embudo || 'recibidos') === 'prospeccion'
                ? 'El lead volverá al embudo de Recibidos. Su estado actual se conserva.'
                : 'El lead pasará al embudo de Prospección. Su estado actual se conserva.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleCambiarEmbudo}>
              Mover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}